import {Card, Divider, Row, Col, message} from 'antd';
import {Link} from "react-router";
import {
    getPlantProducts,
    undoHarvestPlant,
    upsertPlantTrim,
    getPlantTrim,
    deletePlantTrim,
    insertPlantProduct, updatePlantStrain, getPlantStrain,
} from "../../api/Api";
import {useState, useEffect} from "react";
import {UndoHarvestModal} from "./UndoHarvestModal";
import {GramsEntryWidget} from "./GramsEntryWidget";
import {displayToFixedUnit, fixedToDisplayUnit} from "../../util/unitutils";
import {UnitType} from "../../api/Constants";
import {TrashWidget} from "../widget/TrashWidget";
import {ProductSearchWidget} from "../fulfillment/ProductSearchWidget";
import {StrainSelectWidget} from "./StrainSelectWidget";
import {handleNoSession} from "../../util/errorUtils";

export const HarvestedPlantCard = ({plant, session, refresh, onRefresh, onApiFail, props}) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [modalOpen, setModalOpen] = useState(false);

    const [trim, setTrim] = useState({});
    const [plantStrain, setPlantStrain] = useState(undefined);
    const {name, id, description, currentCount, metricTag, strainId, batchName} = plant;
    const io = {onApiFail, refresh, onRefresh};

    useEffect(() => {
        const promises = [];
        promises.push(getPlantProducts(strainId));
        promises.push(getPlantTrim(id));
        Promise.all(promises).then(resultsArray => {
            // result 0 has the trim product template
            const templateResult = resultsArray[0];

            // put all the products into a map
            const nextMap = {};
            templateResult.forEach(p => {
                const {productId} = p;
                nextMap[productId] = {
                    ...p,
                    productId,
                    key: productId,
                    amount: 0
                }
            });

            // overwrite the map with the actual results that have been entered
            const trimResult = resultsArray[1];

            trimResult.forEach(t => {
                const {productId} = t;
                nextMap[productId] = {
                    ...t,
                    key: productId
                }
            });
            setTrim(nextMap);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        // get the plant strain
        getPlantStrain(strainId).then(r => {
            setPlantStrain(r);
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })

    }, [id, strainId, refresh, onApiFail]);

    const undoHarvestModalOk = (notes, createdBy) => {
        undoHarvestPlant(id, "Undo Harvest Plant").then(r => {
            if (onRefresh) {
                onRefresh()
            }
            messageApi.open({
                type: 'success',
                content: `Plants Returned to Harvest Room`,
            });
        }).catch(e => {
            messageApi.open({
                type: 'error',
                content: `Failed to Return Plants`,
            });
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
        setModalOpen(false);
    }

    const undoHarvestModalOpen = () => {
        setModalOpen(true);
    }

    const undoHarvestModalCancel = () => {
        setModalOpen(false);
    }

    const onProductAmountChange = (productId) => (val) => {
        if (val === 0) {
            // guard against setting to 0
            return;
        }

        // convert val to fixed
        const fixedValue = displayToFixedUnit(val, UnitType.Gram);
        upsertPlantTrim(id, productId, fixedValue).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onProductDelete = (productId) => () => {
        deletePlantTrim(id, productId).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onSelect = (item) => {
        insertPlantProduct(strainId, item.id).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onPlantStrainSelect = (val) => {
        console.log(`set plant ${plant.id} strainId ${val.id}`);
        updatePlantStrain(plant.id, val.id).then(r => {
            if (onRefresh) { onRefresh() }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const rowStyle = {
        marginTop: '5px',
        marginBottom: '5px',
    }

    const cardStyle = {
        textAlign: 'left',
        marginTop: '10px',
        marginBottom: '10px',
    }

    const products = Object.values(trim);

    // disable the Undo if we already have recorded items
    let undoDisabled = false;
    products.forEach(p => {
        if (p.amount > 0) {
            undoDisabled = true;
        }
    })

    const cardTitle = <div>
        <Row>
            <Col>
                <div style={{marginRight: '15px'}}>
                <b>{name}</b>
                </div>
            </Col>
            {(!undoDisabled) &&
            <Col>
                <TrashWidget onDelete={undoHarvestModalOpen} />
            </Col>
            }
        </Row>
    </div>

    const strainUnassigned = ((strainId === undefined) || (strainId === null));

    return <div>
        {contextHolder}
        <UndoHarvestModal user={session} open={modalOpen} onOk={undoHarvestModalOk} onCancel={undoHarvestModalCancel} />
        <Card style={cardStyle} title={cardTitle}>
            <Row style={rowStyle}>
                <Col span={3}>
                    Name:
                </Col>
                <Col>
                    <Link to={`/cultivationapp/plant/${id}`}>{name}</Link>
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={3}>
                    <b>Description:</b>
                </Col>
                <Col>
                    {description}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={3}>
                    <b>Count:</b>
                </Col>
                <Col>
                    {currentCount}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={3}>
                    <b>Metrc:</b>
                </Col>
                <Col>
                    {metricTag}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={3}>
                    <b>Batch:</b>
                </Col>
                <Col>
                    {batchName}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={3}>
                    <b>Strain:</b>
                </Col>
                {(strainUnassigned) &&
                    <Col>
                    <div style={{color: 'red'}}>
                        Please assign a Strain to this Plant.
                    </div>
                    </Col>
                }
                {(plantStrain !== undefined) &&
                    <Col>
                        {plantStrain.name}
                    </Col>
                }
            </Row>
            {(strainUnassigned) &&
                <Row style={rowStyle}>
                    <Col span={3}></Col>
                    <Col>
                        <StrainSelectWidget strain={plant.strainId} onSelect={onPlantStrainSelect} io={io} defaultActive={false} />
                    </Col>
                </Row>
            }
            <Divider />
            <Row style={{...rowStyle, fontSize: '16px'}}>
                <b>Enter the Trimmed Products for {name}</b>
            </Row>
            {
                products.map((p, index) => {
                    const {productId, name: productName} = p;
                    let {amount} = p;
                    amount = fixedToDisplayUnit(amount, UnitType.Gram);
                    return <div key={index}>
                        <Row>
                            <Col span={6}>
                                {productName}
                            </Col>
                            <Col span={4}>
                                <GramsEntryWidget value={amount} placeholder={'Enter grams trimmed'} addonAfter={'g'} min={0} precision={1}
                                    onChange={onProductAmountChange(productId)}/>
                            </Col>
                            { (amount > 0) &&
                                <TrashWidget onDelete={onProductDelete(productId)} />
                            }
                        </Row>
                    </div>
                })
            }
            <Divider />
            {(!strainUnassigned) &&
            <Row style={rowStyle}>
                <b>Add another product</b>
            </Row>
            }
            {(!strainUnassigned) &&
            <Row style={rowStyle}>
                <ProductSearchWidget onSelect={onSelect} onApiFail={onApiFail} refresh={refresh} type={"wholesale"} />
            </Row>
            }
            {(strainUnassigned) &&
                <div style={{color: 'red'}}>Please Assign a Strain before adding more products</div>
            }
        </Card>
    </div>
}